import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import blogService from '../../services/blogService';

// Async thunks for admin Blog operations
export const fetchAdminBlogs = createAsyncThunk(
  'blogAdmin/fetchAdminBlogs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await blogService.getAdminBlogs(params);
      // Response shape: { success: true, data: [], total, currentPage, totalPages }
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch blogs');
    }
  }
);

export const fetchAdminBlogBySlug = createAsyncThunk(
  'blogAdmin/fetchAdminBlogBySlug',
  async (slug, { rejectWithValue }) => {
    try {
      const response = await blogService.getAdminBlogBySlug(slug);
      // Response shape for detail: { success: true, data: { id, title, page_content, image_url, ... } }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch blog');
    }
  }
);

export const createAdminBlog = createAsyncThunk(
  'blogAdmin/createAdminBlog',
  async (blogData, { rejectWithValue }) => {
    try {
      const response = await blogService.createBlog(blogData);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create blog');
    }
  }
);

export const updateAdminBlog = createAsyncThunk(
  'blogAdmin/updateAdminBlog',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await blogService.updateBlog(id, data);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update blog');
    }
  }
);

export const deleteAdminBlog = createAsyncThunk(
  'blogAdmin/deleteAdminBlog',
  async (id, { rejectWithValue }) => {
    try {
      await blogService.deleteBlog(id);
      return { id };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete blog');
    }
  }
);

// Initial state
const initialState = {
  blogs: [],
  currentBlog: null,
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    total_pages: 1
  },
  loading: false,
  error: null,
  success: false
};

// Blog Admin slice
const blogAdminSlice = createSlice({
  name: 'blogAdmin',
  initialState,
  reducers: {
    clearBlogError: (state) => {
      state.error = null;
    },
    clearBlogSuccess: (state) => {
      state.success = false;
    },
    resetCurrentBlog: (state) => {
      state.currentBlog = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Blogs
      .addCase(fetchAdminBlogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminBlogs.fulfilled, (state, action) => {
        state.blogs = action.payload.data || [];
        state.pagination = {
           current_page: action.payload.currentPage || 1,
           total_pages: action.payload.totalPages || 1,
           total: action.payload.total || 0,
           per_page: state.pagination.per_page // Keep current
        };
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAdminBlogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch Blog By Slug
      .addCase(fetchAdminBlogBySlug.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminBlogBySlug.fulfilled, (state, action) => {
        // Payload is the blog object directly: { id, title, page_content, image_url, ... }
        state.currentBlog = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAdminBlogBySlug.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create Blog
      .addCase(createAdminBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createAdminBlog.fulfilled, (state, action) => {
        if (action.payload) {
             state.blogs.unshift(action.payload); // Add new blog to the top
        }
        state.loading = false;
        state.success = true;
      })
      .addCase(createAdminBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Update Blog
      .addCase(updateAdminBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateAdminBlog.fulfilled, (state, action) => {
        if (action.payload && action.payload.id) {
            const index = state.blogs.findIndex(b => b.id === action.payload.id);
            if (index !== -1) {
                state.blogs[index] = action.payload;
            }
        }
        state.currentBlog = action.payload;
        state.loading = false;
        state.success = true;
      })
      .addCase(updateAdminBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // Delete Blog
      .addCase(deleteAdminBlog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAdminBlog.fulfilled, (state, action) => {
        state.blogs = state.blogs.filter(b => b.id !== action.payload.id);
        state.loading = false;
        state.success = true;
      })
      .addCase(deleteAdminBlog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearBlogError, clearBlogSuccess, resetCurrentBlog } = blogAdminSlice.actions;
export default blogAdminSlice.reducer;
